import React, { useState } from 'react';
import { useUserStatus } from '../../contexts/UserStatusContext';
import { useDebug } from '../../contexts/DebugContext';
import { SubscriptionService } from '../../Services/SubscriptionService';

const RemainingLinksDebugPanel = () => {
  const { isDebugVisible } = useDebug();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const { 
    userStatus, 
    remainingLinks, 
    setStatus,
    resetRemainingLinks,
    getRemainingLinksText,
    manualDecrementRemainingLinks,
    canCreateLinks,
    refreshUserStatus
  } = useUserStatus();

  const handleSyncWithStripe = async () => {
    setIsSyncing(true);
    setSyncMessage('');
    
    try {
      console.log('🔄 [Debug] Syncing subscription status with Stripe...');
      const response = await SubscriptionService.syncFromStripe();
      const result = response?.data || response;
      
      console.log('✅ [Debug] Stripe sync result:', result);
      
      if (result?.userStatus) {
        setSyncMessage(`✅ Synced! Status updated to: ${result.userStatus}`);
        
        // Refresh the user status context
        await refreshUserStatus();
        
        // Clear message after 3 seconds
        setTimeout(() => setSyncMessage(''), 3000);
      } else {
        setSyncMessage('⚠️ Sync completed but no status change detected');
        setTimeout(() => setSyncMessage(''), 3000);
      }
    } catch (error) {
      console.error('❌ [Debug] Stripe sync error:', error);
      setSyncMessage(`❌ Sync failed: ${error.message || 'Unknown error'}`);
      setTimeout(() => setSyncMessage(''), 5000);
    } finally {
      setIsSyncing(false);
    }
  };

  if (!isDebugVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '80px',
        left: '20px',
        backgroundColor: '#fff',
        border: '2px solid #10b981',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        minWidth: '300px',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Header - Always visible */}
      <div 
        style={{ 
          padding: '12px 16px',
          backgroundColor: '#10b981',
          color: 'white',
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px' }}>⚡</span>
          <span style={{ fontWeight: '600', fontSize: '14px' }}>Link Generations Debug</span>
        </div>
        <span style={{ 
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          ▼
        </span>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div style={{ padding: '16px' }}>
          {/* Current Status */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              padding: '12px',
              backgroundColor: userStatus === 'pro' ? '#ecfdf5' : '#fef3c7',
              border: `1px solid ${userStatus === 'pro' ? '#10b981' : '#f59e0b'}`,
              borderRadius: '6px'
            }}>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                color: userStatus === 'pro' ? '#10b981' : '#f59e0b',
                marginBottom: '4px'
              }}>
                Status: {userStatus === 'pro' ? 'Pro Plan' : 'Starter Plan'}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#666',
                marginBottom: '4px'
              }}>
                Remaining: <strong>{getRemainingLinksText()}</strong>
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: canCreateLinks() ? '#10b981' : '#ef4444',
                fontWeight: '500'
              }}>
                Can create links: {canCreateLinks() ? '✅ Yes' : '❌ No'}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setStatus('starter')}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  backgroundColor: userStatus === 'starter' ? '#f59e0b' : 'transparent',
                  color: userStatus === 'starter' ? 'white' : '#f59e0b',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Starter
              </button>
              <button
                onClick={() => setStatus('pro')}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  backgroundColor: userStatus === 'pro' ? '#10b981' : 'transparent',
                  color: userStatus === 'pro' ? 'white' : '#10b981',
                  border: '1px solid #10b981',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Pro
              </button>
            </div>

            {/* Link Management Controls */}
            {userStatus === 'starter' && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={manualDecrementRemainingLinks}
                  disabled={remainingLinks === 0}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    backgroundColor: remainingLinks > 0 ? '#ef4444' : 'transparent',
                    color: remainingLinks > 0 ? 'white' : '#999',
                    border: `1px solid ${remainingLinks > 0 ? '#ef4444' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: remainingLinks > 0 ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    opacity: remainingLinks > 0 ? 1 : 0.5
                  }}
                >
                  Use 1 Link
                </button>
                <button
                  onClick={resetRemainingLinks}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Reset to 2
                </button>
              </div>
            )}

            {/* Stripe Sync Button - Always visible */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSyncWithStripe}
                disabled={isSyncing}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  backgroundColor: isSyncing ? '#6b7280' : '#3b82f6',
                  color: 'white',
                  border: '1px solid #3b82f6',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: isSyncing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isSyncing ? 0.7 : 1
                }}
              >
                {isSyncing ? '🔄 Syncing...' : '🔄 Sync with Stripe'}
              </button>
            </div>

            {/* Sync Message */}
            {syncMessage && (
              <div style={{
                padding: '8px',
                backgroundColor: syncMessage.includes('✅') ? '#ecfdf5' : syncMessage.includes('❌') ? '#fef2f2' : '#fef3c7',
                border: `1px solid ${syncMessage.includes('✅') ? '#10b981' : syncMessage.includes('❌') ? '#ef4444' : '#f59e0b'}`,
                borderRadius: '4px',
                fontSize: '11px',
                color: syncMessage.includes('✅') ? '#10b981' : syncMessage.includes('❌') ? '#ef4444' : '#f59e0b',
                fontWeight: '500'
              }}>
                {syncMessage}
              </div>
            )}

            {/* Test Scenarios */}
            <div style={{ 
              marginTop: '8px',
              fontSize: '11px',
              color: '#666',
              padding: '8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px'
            }}>
              <div><strong>Test Scenarios:</strong></div>
              <div>• Toggle between plans</div>
              <div>• Use links to test limit behavior</div>
              <div>• Reset count to test monthly refresh</div>
              <div>• Sync with Stripe to verify subscription status</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemainingLinksDebugPanel;